import {
  ChangeEvent,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import Chevron from 'assets/icons/chevron-sharp.svg?react';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import styles from 'components/Checkbox/Checkbox.module.scss';
import { EventCollectorProps } from 'hooks/context/EventCollector/types';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import CheckboxEvent from 'hooks/context/EventCollector/events/CheckboxEvent';

export type Props = {
  children: ReactNode;
  className?: string;
  classNameContainer?: string;
  classNameContent?: string;
  dataTest?: string;
  disabled?: boolean;
  error?: string;
  isTouched?: boolean;
  name: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: boolean;
  expand?: boolean;
  pending?: boolean;
};

function Checkbox({
  children,
  className,
  classNameContainer,
  classNameContent,
  dataTest = 'checkbox',
  disabled,
  error,
  isTouched,
  name,
  onChange,
  value,
  expand = false,
  eventCollector,
  pending = false,
}: Props & EventCollectorProps) {
  const { t } = useTranslation();
  const { track } = useEventCollector();
  const [expanded, setExpanded] = useState(expand);
  const [expandable, setExpandable] = useState(false);
  const [wasExpanded, setWasExpanded] = useState(false);
  const showError = isTouched && error;

  const handleExpand = useCallback(() => {
    if (!wasExpanded) setWasExpanded(true);

    setExpanded((prev) => !prev);
  }, [wasExpanded]);

  const handleEventCollector = useCallback(
    (checked: boolean) => {
      if (eventCollector?.dontTrack) {
        return;
      }

      track(
        new CheckboxEvent({
          checkboxChecked: checked,
          description: eventCollector?.description ?? name,
        }),
      );
    },
    [eventCollector?.description, eventCollector?.dontTrack, name, track],
  );

  const handleOnChange = useCallback(
    (e: ChangeEvent<any>) => {
      if (expandable && !wasExpanded && !expand) {
        handleExpand();
        return;
      }

      if (onChange) {
        onChange(e);
        handleEventCollector(e.target.checked);
      }
    },
    [
      expandable,
      wasExpanded,
      expand,
      onChange,
      handleExpand,
      handleEventCollector,
    ],
  );

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current && containerRef.current.clientHeight > 120) {
      setExpandable(true);
    }
  }, [children]);

  return (
    <div
      className={classes(className, styles.wrapper)}
      data-test={`${dataTest}-layout`}
    >
      <label
        className={classes(
          styles.container,
          classNameContainer,
          (expanded || !expandable) && styles.expanded,
          pending && styles.pending,
        )}
        htmlFor={name}
      >
        <input
          type="checkbox"
          name={name}
          id={name}
          onChange={handleOnChange}
          value={String(value)}
          checked={value}
          data-test={dataTest}
          disabled={disabled || pending}
          className={styles.invisible}
        />
        {!expanded && expandable && <div className={styles.overlay} />}
        <div
          ref={containerRef}
          data-test={`${dataTest}-content`}
          className={classNameContent}
        >
          {children}
        </div>
        <span
          className={classes(styles.checkmark, showError && styles.error)}
        />
      </label>
      {expandable && !expand && (
        <div
          className={styles['read-more']}
          onClick={handleExpand}
          data-test={`${dataTest}-not-expanded`}
        >
          <Chevron className={classes(styles.chevron, expanded && styles.up)} />
          <span className={styles['read-more-label']}>
            {expanded ? t('common:show_less') : t('common:show_more')}
          </span>
        </div>
      )}
      {showError && (
        <div className={styles['error-message']}>
          <ErrorMessage errors={error} dataTest={`${dataTest}-error`} />
        </div>
      )}
    </div>
  );
}

const CheckboxMemo = memo(Checkbox);

export default CheckboxMemo;
